// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  Cancel as RejectedIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassEmpty as PendentIcon
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignProcess from '../model/SignProcess';

const maxSize: number = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  loading: boolean,
  disabled: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: ({ fileType: string, file: File }) => void,
  onDelete: ({ fileType: string }) => void,
};

const ProcessDocumentData = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { code, process, expanded, loading, disabled, onExpanded, onSave, onDelete } = props;

  const [files, setFiles] = useState<any>({});

  let processStatusText = process.hasDocuments() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasDocuments() ? <DoneIcon color="action"/> : <EditIcon color="secondary"/>;
  if (process.hasDocumentsValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary"/>;
  }

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
      >
        {processStatusIcon}&nbsp;
        <Typography className={classes.heading}>
          {t('Documents data')}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t(processStatusText)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <List>
          {process.files.map((item) => (
            <ListItem key={`${item.fileType}-${item.name}`}>
              <Typography
                variant="subtitle2"
                className={classes.grey}>
                {item.approved ? <DoneIcon color="primary"/> : (item.rejected ? <RejectedIcon color="secondary"/> : <PendentIcon/>)}
                {t(`Document ${item.fileType}`)}: <b>{item.name}</b>&nbsp;&nbsp;
                {item.message && <Chip label={item.message} color={item.approved ? 'primary' : 'secondary'} />}
                {(!disabled || item.rejected) && <IconButton
                  aria-label="delete"
                  onClick={() => {
                    onDelete({
                      fileType: item.fileType,
                    });
                  }}
                  className={classes.error}>
                  <DeleteIcon/>
                </IconButton>}
              </Typography>
            </ListItem>
          ))}
          {process.pendentDocumentTypes().map((item, index) => {
            const id = `upload-document-${item}-${index}`;
            let acceptTypes = '.png,.jpg';
            if (item !== 'LEGAL_ID' && item !== 'LEGAL_ID_BACK') {
              acceptTypes += ',.pdf';
            }
            return (
              <ListItem key={id}>
                <input
                  accept={acceptTypes}
                  className={classes.upload}
                  id={id}
                  type="file"
                  onChange={(e) => {
                    if (e.target.files
                      && e.target.files.length) {
                      const file = e.target.files[ 0 ];
                      if (file.size >= maxSize) {
                        enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
                          variant: 'error',
                        });
                      } else {
                        let docs = { ...files };
                        docs[ item ] = file;
                        setFiles(docs);
                      }
                    }
                  }}
                />
                <label htmlFor={id}>
                  {!files[ item ] && <Button
                    disabled={disabled}
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon/>}
                    component="span">
                    {t(`Add document ${item}`)}
                  </Button>}
                  {!!files[ item ] && <span>
                  <Typography variant="subtitle2" className={classes.grey}>
                    {t(`Document ${item}`)}: {files[ item ].name}&nbsp;
                    <Button
                      disabled={disabled}
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress color="inherit" size="1rem" /> : <UploadIcon/>}
                      aria-label="upload"
                      onClick={() => {
                        onSave({
                          fileType: item,
                          file: files[ item ],
                        });
                      }}
                    >
                      {t('Upload')}
                    </Button>
                    <IconButton
                      aria-label="delete"
                      disabled={disabled}
                      className={classes.error}
                      onClick={() => {
                        let docs = { ...files };
                        delete docs[ item ];
                        setFiles(docs);
                      }}>
                      <DeleteIcon/>
                    </IconButton>
                  </Typography>
                </span>}
                </label>
              </ListItem>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessDocumentData;
